import React, { useState, useEffect } from 'react';
import i18n from '../../i18';
import {
    Box,
    TextField,
    Typography,
    InputAdornment,
    CircularProgress
} from '@mui/material';
// @mui

export default class DialogAmountField extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            exchangeRate: null,
            loading: false,
            error: null,
            chfInput: ''
        };
    }

    async componentDidMount() {
        await this.fetchExchangeRate();
        this.syncChfFromSilbar(this.props.value);
    }

    componentDidUpdate(prevProps, prevState) {
        // When exchange rate becomes available the first time, initialise CHF display
        if (
            prevState.exchangeRate === null &&
            this.state.exchangeRate !== null &&
            this.props.value
        ) {
            this.syncChfFromSilbar(this.props.value);
        }
    }

    syncChfFromSilbar(value) {
        if (value === null || value === undefined || !this.state.exchangeRate)
            return;
        const userAmount = (
            parseFloat(value) / this.state.exchangeRate
        ).toFixed(2);
        this.setState({ chfInput: userAmount });
    }

    async fetchExchangeRate() {
        try {
            this.setState({ loading: true, error: null });

            // Get the server instance from context
            const server = this.props.context?.getState()?.server;
            if (!server?.currencyExchangeRates) {
                throw new Error(
                    'Currency exchange rates service not available'
                );
            }

            // Get user's currency from localStorage
            const userCurrency = localStorage.currency || 'CHF';

            // Fetch XAG to user's currency exchange rate
            const response = await server.currencyExchangeRates.getAll();

            if (response && response.data) {
                const pair = response.data.find(
                    (r) =>
                        r.currencyExchangePair?.fromCurrency?.shortName ===
                            'XAG' &&
                        r.currencyExchangePair?.toCurrency?.shortName ===
                            userCurrency
                );

                if (pair) {
                    // API gives XAG -> userCurrency. We need userCurrency -> SILbar(XAG), so invert.
                    const inverted = 1 / parseFloat(pair.rate);
                    this.setState({ exchangeRate: inverted, loading: false });
                } else {
                    // No exchange rate found
                    this.setState({
                        exchangeRate: null,
                        loading: false
                    });
                }
            } else {
                throw new Error('Failed to fetch exchange rates');
            }
        } catch (error) {
            console.error('Error fetching exchange rate:', error);
            // No exchange rate available
            this.setState({
                exchangeRate: null,
                loading: false,
                error: error.message
            });
        }
    }

    render() {
        const translatedLabel = i18n.t(`columns.${this.props.field.label}`);
        const placeholderLabel = i18n.t(
            `placeholder.${this.props.field.label}`
        );
        const required = this.props.field.required;
        const requiredLabel = required ? ' *' : '';

        const userAmount = this.state.chfInput
            ? parseFloat(this.state.chfInput)
            : 0;

        // Get user's currency from localStorage
        const userCurrency = localStorage.currency || 'CHF';

        // Calculate SILbar equivalent using fetched exchange rate
        const silbarAmount = this.state.exchangeRate
            ? (userAmount * this.state.exchangeRate).toFixed(2)
            : null;

        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                    {translatedLabel + requiredLabel}
                </Typography>
                <TextField
                    placeholder={placeholderLabel}
                    key={this.props.field.id}
                    margin="dense"
                    id={this.props.field.id}
                    type="number"
                    value={this.state.chfInput}
                    fullWidth
                    disabled={
                        !!this.props.field.readOnly ||
                        !!this.props.field.disabled
                    }
                    inputProps={{ step: '0.01' }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: '#BA9B61'
                                    }}
                                >
                                    {userCurrency}
                                </Typography>
                            </InputAdornment>
                        ),
                        readOnly:
                            !!this.props.field.readOnly ||
                            !!this.props.field.disabled
                    }}
                    onChange={(event) => {
                        const inputValue = event.target.value;
                        this.setState({ chfInput: inputValue });
                        if (this.state.exchangeRate && inputValue !== '') {
                            const sil =
                                parseFloat(inputValue) *
                                this.state.exchangeRate;
                            this.props.onChange(this.props.field.id, sil);
                        } else {
                            this.props.onChange(this.props.field.id, '');
                        }
                    }}
                    onBlur={(event) => {
                        const v = event.target.value;
                        if (v === '' || v === null || v === undefined) return;
                        const num = parseFloat(v);
                        if (!Number.isNaN(num)) {
                            if (this.state.exchangeRate) {
                                const sil = num * this.state.exchangeRate;
                                this.props.onChange(this.props.field.id, sil);
                            }
                        }
                    }}
                />

                {/* Exchange rate info and SILbar equivalent */}
                <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                    {this.state.loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 1
                            }}
                        >
                            <CircularProgress size={12} />
                            <Typography
                                variant="caption"
                                sx={{ color: '#98A2B3' }}
                            >
                                {i18n.t('text.loadingExchangeRate')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {silbarAmount && (
                                <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: '#98A2B3' }}
                                >
                                    {`≈ ${silbarAmount} SILbar`}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        );
    }
}
